import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { Domain } from "./Tile";

interface DimmerSliderProps {
  /** 0..1 */
  value: number;
  onChange: (value: number) => void;
  onCommit?: (value: number) => void;
  domain?: Domain;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

const fillColor: Record<Domain, string> = {
  neutral: "bg-foreground/60",
  light: "bg-domain-light",
  climate: "bg-domain-climate",
  energy: "bg-domain-energy",
  security: "bg-domain-security",
  flow: "bg-domain-flow",
};

export function DimmerSlider({
  value,
  onChange,
  onCommit,
  domain = "light",
  disabled,
  className,
  "aria-label": ariaLabel = "Dim level",
}: DimmerSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const latest = useRef(value);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    latest.current = value;
  }, [value]);

  const ratioFromPointer = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return value;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  return (
    <div
      ref={trackRef}
      data-slot="dimmer-slider"
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      aria-disabled={disabled || undefined}
      aria-valuetext={`${Math.round(value * 100)}%`}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "relative h-8 w-full touch-none overflow-hidden rounded-full bg-muted outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
        latest.current = ratioFromPointer(e.clientX);
        onChange(latest.current);
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        latest.current = ratioFromPointer(e.clientX);
        onChange(latest.current);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        e.currentTarget.releasePointerCapture(e.pointerId);
        setDragging(false);
        onCommit?.(latest.current);
      }}
      onPointerCancel={() => {
        setDragging(false);
        onCommit?.(latest.current);
      }}
      onKeyDown={(e) => {
        let next: number | null = null;
        if (e.key === "ArrowRight") next = Math.min(1, value + 0.05);
        else if (e.key === "ArrowLeft") next = Math.max(0, value - 0.05);
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = 1;
        if (next === null) return;
        e.preventDefault();
        e.stopPropagation();
        latest.current = next;
        onChange(next);
        onCommit?.(next);
      }}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 rounded-full transition-[width] duration-75",
          fillColor[domain],
        )}
        style={{ width: `${(dragging ? latest.current : value) * 100}%` }}
      />
    </div>
  );
}
